import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useAccessToken } from '@/services/auth';
import { approvalNumbersOptions } from '@/services/lookups';
import { ApprovalNumberResponse } from '@/services/lookups/types';

type Props = {
  value?: string | null;
  onChange?: (item: ApprovalNumberResponse | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

export const ApprovalNumberAutoComplete = ({
  value = null,
  onChange,
  placeholder = 'Select approval number',
  className,
  disabled,
}: Props) => {
  const token = useAccessToken();
  const { data: items = [], isLoading } = useQuery(
    approvalNumbersOptions(token),
  );
  const [selected, setSelected] = useState<ApprovalNumberResponse | null>(null);

  useEffect(() => {
    if (!items.length || !value) {
      if (!value) setSelected(null);
      return;
    }
    const found = items.find((item) => item.id === value);
    if (found && found.id !== selected?.id) {
      setSelected(found);
    }
  }, [items, value]);

  const commit = useMemo(
    () => (item: ApprovalNumberResponse | null) => {
      setSelected(item);
      onChange?.(item);
    },
    [onChange],
  );

  return (
    <Combobox
      items={items}
      autoComplete="none"
      value={selected}
      onValueChange={commit}
      itemToStringValue={(item: ApprovalNumberResponse) => item.id}
      itemToStringLabel={(item: ApprovalNumberResponse) => item.name ?? ''}
      disabled={disabled}
    >
      <ComboboxInput
        className={className}
        placeholder={isLoading ? 'Loading...' : placeholder}
        showClear
      />
      <ComboboxContent>
        <ComboboxEmpty>No approval numbers found.</ComboboxEmpty>
        <ComboboxList>
          {(item: ApprovalNumberResponse) => (
            <ComboboxItem key={item.id} value={item}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
